import { useMemo, useRef, useState } from 'react';
import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import InputSearch from '@zvuk-b2b/react-uikit/ui/InputSearch';
import classNames from 'classnames';
import { PaginationInfoType } from '@shared/api/graphql/generated';
import Text from '@zvuk-b2b/react-uikit/ui/Text';

import CompanyTagsList from 'application/components/MainToolbar/FiltersTool/TagFilters/CompanyTagsList';
import { VISIBLE_TITLE_LENGTH, VISIBLE_TITLE_LENGTH_MOBILE } from 'application/components/CompanyTagChip';
import useMedia from '@zvuk-b2b/react-uikit/toolkit/useMedia';
import TagFilters, { ITagFiltersState } from 'application/components/MainToolbar/FiltersTool/TagFilters';
import ChooseItemList from './ChooseItemList';
import { SelectorType } from './ChooseItemList/ChooseItemListItem';
import TEXTS from './texts';
import styles from './index.module.scss';
import { PointsListCheckboxGroupPointType } from '../types';

export type SearchItemsType = {
  items: Array<PointsListCheckboxGroupPointType>;
  selectedItems: Array<PointsListCheckboxGroupPointType>;
  isLoading: boolean;
  isReloading?: boolean;
  hasSearch?: boolean;
  hasMultiselect?: boolean;
  className?: string;
  onChange: (items: Array<PointsListCheckboxGroupPointType>) => void;
  loadMore?: () => void;
  paginationSettings?: PaginationInfoType;
  selectorType: SelectorType;
  onChangeWrapper: (value: boolean, item: PointsListCheckboxGroupPointType) => void;
  emptySearchText: string;
  inputPlaceholder?: string;
  deviceGroupId?: string;
};

const SearchItems = ({
  items,
  isLoading,
  isReloading,
  selectedItems,
  hasMultiselect = false,
  hasSearch = false,
  onChange,
  className,
  loadMore,
  paginationSettings,
  selectorType,
  onChangeWrapper,
  emptySearchText,
  inputPlaceholder,
  deviceGroupId,
}: SearchItemsType) => {
  const [search, setSearch] = useState('');
  const [companyListTagsProps, setCompanyListTagsProps] = useState<ITagFiltersState>({
    selectedTags: [],
  });

  const listRef = useRef<HTMLInputElement | null>(null);

  const { isDesktop } = useMedia();

  const currentItems = useMemo(() => {
    const intemsWithSearch = items.filter((item) =>
      (item?.address || item.title)?.toLowerCase().includes(search.toLowerCase())
    );
    const tags = new Set(companyListTagsProps.allSelectedTags?.map((it) => String(it.key)));

    const resultItems = companyListTagsProps.selectedTags?.length
      ? intemsWithSearch.filter((it) => {
          if (!it.tags?.length) return false;

          const pointTags = new Set(it.tags?.map((it) => String(it.id)));

          return tags.isSubsetOf(pointTags);
        })
      : intemsWithSearch;

    return resultItems.map((it) => ({
      ...it,
      isDisabled: Boolean(it.device?.group?.id && String(it.device?.group.id) !== String(deviceGroupId)),
    }));
  }, [items, search, companyListTagsProps.selectedTags]);

  const selectedItemsList = currentItems.filter((item) =>
    selectedItems.map((selectedItem) => String(selectedItem.id)).includes(String(item.id))
  );

  const hasSelectedItems = selectedItemsList.length > 0;
  const hasUnselectedItems = currentItems.length > selectedItemsList.length;

  const onChangeAll = (value: boolean) => {
    if (value) {
      onChange(currentItems.filter((it) => !it.isDisabled));
      return;
    }

    const filteredItems = selectedItems.filter((current) => !currentItems.some((item) => item.id === current.id));

    onChange(filteredItems);
  };

  return (
    <div className={classNames(styles.SearchItems, className)}>
      {hasSearch && SelectorType.dropdown !== selectorType && (
        <div className={styles.filtersWrapper}>
          <InputSearch
            containerClassName={styles.SearchItemsInput}
            value={search}
            elementId="search_input_items_list"
            onChange={setSearch}
            isDisabled={isLoading || isReloading}
            placeholder={inputPlaceholder}
          />
          <TagFilters
            pageType="point"
            buttonClassName={styles.dropdownButton}
            withList={false}
            onlyIcon
            onSetCompanyListTagsProps={setCompanyListTagsProps}
            listRef={listRef}
          />
        </div>
      )}

      {companyListTagsProps && (
        <div
          style={companyListTagsProps.showAllTags ? { flexWrap: 'wrap' } : undefined}
          id="tags_id"
          ref={listRef}
          className={styles.companyTagsListWrapper}>
          <CompanyTagsList
            onDisableTag={companyListTagsProps?.onDisableTag!}
            visibleTitleLength={isDesktop ? VISIBLE_TITLE_LENGTH : VISIBLE_TITLE_LENGTH_MOBILE}
            selectedTags={companyListTagsProps?.selectedTags!}
            showAllTags={companyListTagsProps?.showAllTags!}
            moreTagsCount={companyListTagsProps.moreTagsCount!}
            expandCallback={companyListTagsProps.expandCallback!}
            clearAll={companyListTagsProps.clearAll!}
          />
        </div>
      )}
      <div className={styles.actionsRow}>
        <Text
          isLoading={!currentItems.length && isLoading}
          elementId="points_list_items_count">
          {TEXTS.SELECTED} {selectedItems.length}
        </Text>
        {hasMultiselect && (
          <div className={styles.SearchItemsMultiselect}>
            {hasUnselectedItems && (
              <Button
                elementId="items_list_select_all"
                type={ButtonType.PLAIN}
                className={styles.SearchItemsMultiselectButton}
                onClick={() => onChangeAll(true)}
                isDisabled={isLoading || isReloading}>
                {TEXTS.ITEM_LIST_SELECT_ALl}
              </Button>
            )}
            {hasUnselectedItems && hasSelectedItems && <span className={styles.SearchItemsMultiselectPoint}>•</span>}
            {hasSelectedItems && (
              <Button
                elementId="items_list_select_none"
                type={ButtonType.PLAIN}
                className={styles.SearchItemsMultiselectButton}
                onClick={() => onChangeAll(false)}
                isDisabled={isLoading || isReloading}>
                {TEXTS.ITEM_LIST_SELECT_NONE}
              </Button>
            )}
          </div>
        )}
      </div>

      {currentItems.length === 0 && !isLoading && <div className={styles.SearchItemsEmpty}>{emptySearchText}</div>}
      <div className={styles.listWrapper}>
        <ChooseItemList
          itemList={currentItems}
          isLoading={isLoading}
          isReloading={isReloading}
          selectedItems={selectedItems}
          selectorType={selectorType}
          onChange={(value: boolean, point: PointsListCheckboxGroupPointType) => {
            onChangeWrapper(value, point);
          }}
          loadMore={loadMore}
          paginationSettings={paginationSettings}
        />
      </div>
    </div>
  );
};

export default SearchItems;
